import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { paceDataAPI } from '../services/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, RefreshCw, AlertCircle, Info, ChevronUp, ChevronDown, X, User, Calendar } from 'lucide-react';

function PaceOfPlayManagement() {
  const [searchParams] = useSearchParams();
  const [analytics, setAnalytics] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [membersList, setMembersList] = useState([]);
  const [clubInsights, setClubInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [cacheStatus, setCacheStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedView, setSelectedView] = useState('club-summary');
  const [sortConfig, setSortConfig] = useState({ key: 'behindRank', direction: 'asc' });
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [loadingEventDetail, setLoadingEventDetail] = useState(false);

  useEffect(() => {
    loadAllData();

    // Check if we should auto-trigger sync from URL params
    if (searchParams.get('action') === 'sync') {
      handleBulkSync();
    }
  }, [searchParams]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [analyticsRes, cacheRes, eventsRes, membersRes, insightsRes] = await Promise.allSettled([
        paceDataAPI.getPaceAnalytics(),
        paceDataAPI.getCacheStatus(),
        paceDataAPI.getRecentEvents(),
        paceDataAPI.getMembersList(),
        paceDataAPI.getClubInsights()
      ]);

      if (analyticsRes.status === 'fulfilled') {
        setAnalytics(analyticsRes.value.data.data);
      }
      if (cacheRes.status === 'fulfilled') {
        setCacheStatus(cacheRes.value.data.data);
      }
      if (eventsRes.status === 'fulfilled') {
        setRecentEvents(eventsRes.value.data.data?.events || []);
      }
      if (membersRes.status === 'fulfilled') {
        setMembersList(membersRes.value.data.data?.members || []);
      }
      if (insightsRes.status === 'fulfilled') {
        setClubInsights(insightsRes.value.data.data);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.error || 'Failed to load pace analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  };

  const handleBulkSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      const response = await paceDataAPI.syncAllPaceData();
      console.log('Bulk sync completed:', response.data);
      await loadAllData();
      alert(`Bulk sync completed! ${response.data.data.recordsCached} records cached.`);
    } catch (err) {
      console.error('Error during bulk sync:', err);
      setError(err.response?.data?.error || 'Failed to sync pace data');
    } finally {
      setSyncing(false);
    }
  };

  // Get row class based on pace rating (Balboa style)
  const getRowClass = (ratingClass) => {
    switch (ratingClass) {
      case 'success': return 'bg-green-50 dark:bg-green-950/30';
      case 'warning': return 'bg-yellow-50 dark:bg-yellow-950/30';
      case 'danger': return 'bg-red-50 dark:bg-red-950/30';
      default: return '';
    }
  };

  // Sort members
  const sortedMembers = [...membersList].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];

    // Handle string comparisons
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal?.toLowerCase() || '';
    }

    // Handle null/undefined
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    if (sortConfig.direction === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    }
    return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="h-3 w-3 inline ml-1" /> :
      <ChevronDown className="h-3 w-3 inline ml-1" />;
  };

  // Load member detail when clicked
  const handleMemberClick = async (member) => {
    setSelectedMember(member);
    setLoadingDetail(true);
    try {
      const response = await paceDataAPI.getMemberDetail(member.ghin);
      if (response.data.success) {
        setMemberDetail(response.data.data);
      }
    } catch (err) {
      console.error('Error loading member detail:', err);
      setMemberDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeMemberDetail = () => {
    setSelectedMember(null);
    setMemberDetail(null);
  };

  // Load event detail when clicked
  const handleEventClick = async (event) => {
    // Create event key: eventName-YYYY-MM-DD
    const eventKey = `${event.eventName}-${new Date(event.eventDate).toISOString().split('T')[0]}`;
    setSelectedEvent({ ...event, eventKey });
    setLoadingEventDetail(true);
    try {
      const response = await paceDataAPI.getEventDetail(eventKey);
      if (response.data.success) {
        setEventDetail(response.data.data);
      }
    } catch (err) {
      console.error('Error loading event detail:', err);
      setEventDetail(null);
    } finally {
      setLoadingEventDetail(false);
    }
  };

  const closeEventDetail = () => {
    setSelectedEvent(null);
    setEventDetail(null);
  };

  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-6 w-96" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Back Navigation */}
        <Link
          to="/dashboard"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Pace of Play</h1>
            <p className="text-muted-foreground mt-1">
              Monitor pace of play performance for PARDS members
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {cacheStatus && (
              <span className="text-sm text-muted-foreground">
                {cacheStatus.recordCount} players synced
              </span>
            )}
            <Button
              variant="outline"
              onClick={handleBulkSync}
              disabled={syncing}
            >
              {syncing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                'Sync from Balboa'
              )}
            </Button>
            <Button
              variant="secondary"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={loadAllData}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content with Tabs - Balboa Style */}
        <Tabs value={selectedView} onValueChange={setSelectedView}>
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
            <TabsTrigger value="club-summary">Club Summary</TabsTrigger>
            <TabsTrigger value="recent-events">Recent Events</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
          </TabsList>

          {/* Club Summary Tab */}
          <TabsContent value="club-summary" className="space-y-6">
            {/* Why Pace of Play Matters - Info Panel */}
            <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                  <Info className="h-5 w-5" />
                  Why Pace of Play Matters?
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2 text-blue-900 dark:text-blue-200">
                <p>
                  Pace of Play is critical to the enjoyment of the game. It affects everyone on the course - from the groups ahead to the groups behind.
                </p>
                <p>
                  <strong>POP Rating</strong> measures how a player or group performs relative to the expected pace. A rating of <strong>0.00</strong> means exactly on pace,
                  <strong className="text-green-600"> negative values</strong> mean faster than pace, and
                  <strong className="text-red-600"> positive values</strong> mean slower than pace.
                </p>
                <p>
                  Players with consistently high POP ratings may be contacted by the Tournament Director to discuss ways to improve their pace of play.
                </p>
              </CardContent>
            </Card>

            {/* Club Pace of Play Averages - Weekday vs Weekend */}
            {clubInsights && clubInsights.clubAverages && (
              <Card>
                <CardHeader>
                  <CardTitle>Club Pace of Play Averages</CardTitle>
                  <p className="text-sm text-muted-foreground">Weekday vs Weekend breakdown</p>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left py-3 px-4 font-medium">Period</th>
                          <th className="text-center py-3 px-4 font-medium">Rounds</th>
                          <th className="text-center py-3 px-4 font-medium">First Group</th>
                          <th className="text-center py-3 px-4 font-medium">Avg</th>
                          <th className="text-center py-3 px-4 font-medium">Last Group</th>
                          <th className="text-center py-3 px-4 font-medium">Pace +/-</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clubInsights.clubAverages.weekday && (
                          <tr className="border-b hover:bg-muted/30">
                            <td className="py-3 px-4 font-medium">Weekday</td>
                            <td className="py-3 px-4 text-center">{clubInsights.clubAverages.weekday.roundCount}</td>
                            <td className="py-3 px-4 text-center">{clubInsights.clubAverages.weekday.firstGroup}</td>
                            <td className="py-3 px-4 text-center">{clubInsights.clubAverages.weekday.avg}</td>
                            <td className="py-3 px-4 text-center">{clubInsights.clubAverages.weekday.lastGroup}</td>
                            <td className={`py-3 px-4 text-center font-semibold ${
                              clubInsights.clubAverages.weekday.pacePlusValue <= 5 ? 'text-green-600' :
                              clubInsights.clubAverages.weekday.pacePlusValue <= 10 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {clubInsights.clubAverages.weekday.pacePlus}
                            </td>
                          </tr>
                        )}
                        {clubInsights.clubAverages.weekend && (
                          <tr className="border-b hover:bg-muted/30">
                            <td className="py-3 px-4 font-medium">Weekend</td>
                            <td className="py-3 px-4 text-center">{clubInsights.clubAverages.weekend.roundCount}</td>
                            <td className="py-3 px-4 text-center">{clubInsights.clubAverages.weekend.firstGroup}</td>
                            <td className="py-3 px-4 text-center">{clubInsights.clubAverages.weekend.avg}</td>
                            <td className="py-3 px-4 text-center">{clubInsights.clubAverages.weekend.lastGroup}</td>
                            <td className={`py-3 px-4 text-center font-semibold ${
                              clubInsights.clubAverages.weekend.pacePlusValue <= 5 ? 'text-green-600' :
                              clubInsights.clubAverages.weekend.pacePlusValue <= 10 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {clubInsights.clubAverages.weekend.pacePlus}
                            </td>
                          </tr>
                        )}
                        {clubInsights.clubAverages.clubAverage && (
                          <tr className="border-b bg-muted/20 font-semibold">
                            <td className="py-3 px-4">Club Average</td>
                            <td className="py-3 px-4 text-center">{clubInsights.clubAverages.clubAverage.roundCount}</td>
                            <td className="py-3 px-4 text-center">{clubInsights.clubAverages.clubAverage.firstGroup}</td>
                            <td className="py-3 px-4 text-center">{clubInsights.clubAverages.clubAverage.avg}</td>
                            <td className="py-3 px-4 text-center">{clubInsights.clubAverages.clubAverage.lastGroup}</td>
                            <td className={`py-3 px-4 text-center ${
                              clubInsights.clubAverages.clubAverage.pacePlusValue <= 5 ? 'text-green-600' :
                              clubInsights.clubAverages.clubAverage.pacePlusValue <= 10 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {clubInsights.clubAverages.clubAverage.pacePlus}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pace Distribution Summary */}
            {analytics && (
              <Card>
                <CardHeader>
                  <CardTitle>Player Distribution by Pace</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                      <div className="text-3xl font-bold text-green-600">
                        {analytics.courseAnalytics.behindDistribution?.onPace ||
                         analytics.courseAnalytics.paceDistribution?.fast || 0}
                      </div>
                      <div className="font-medium text-green-700 dark:text-green-300">On Pace</div>
                      <div className="text-xs text-muted-foreground">0-5 min behind</div>
                    </div>
                    <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800">
                      <div className="text-3xl font-bold text-yellow-600">
                        {analytics.courseAnalytics.behindDistribution?.slightBehind ||
                         analytics.courseAnalytics.paceDistribution?.average || 0}
                      </div>
                      <div className="font-medium text-yellow-700 dark:text-yellow-300">Slightly Behind</div>
                      <div className="text-xs text-muted-foreground">5-10 min behind</div>
                    </div>
                    <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800">
                      <div className="text-3xl font-bold text-orange-600">
                        {analytics.courseAnalytics.behindDistribution?.behind ||
                         analytics.courseAnalytics.paceDistribution?.slow || 0}
                      </div>
                      <div className="font-medium text-orange-700 dark:text-orange-300">Behind</div>
                      <div className="text-xs text-muted-foreground">10-15 min behind</div>
                    </div>
                    <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                      <div className="text-3xl font-bold text-red-600">
                        {analytics.courseAnalytics.behindDistribution?.farBehind ||
                         analytics.courseAnalytics.paceDistribution?.verySlow || 0}
                      </div>
                      <div className="font-medium text-red-700 dark:text-red-300">Far Behind</div>
                      <div className="text-xs text-muted-foreground">15+ min behind</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Stats */}
            {analytics && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{analytics.courseAnalytics.activePlayers || 0}</div>
                    <div className="text-sm text-muted-foreground">Active Players</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{analytics.courseAnalytics.totalRounds || 0}</div>
                    <div className="text-sm text-muted-foreground">Total Rounds</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">
                      {analytics.courseAnalytics.averageRoundTime
                        ? `${Math.floor(analytics.courseAnalytics.averageRoundTime / 60)}:${String(Math.round(analytics.courseAnalytics.averageRoundTime % 60)).padStart(2, '0')}`
                        : 'N/A'}
                    </div>
                    <div className="text-sm text-muted-foreground">Avg Round Time</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className={`text-2xl font-bold ${
                      (analytics.courseAnalytics.averageBehind || 0) <= 5 ? 'text-green-600' :
                      (analytics.courseAnalytics.averageBehind || 0) <= 10 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {analytics.courseAnalytics.averageBehind !== undefined
                        ? `${analytics.courseAnalytics.averageBehind > 0 ? '+' : ''}${analytics.courseAnalytics.averageBehind.toFixed(1)} min`
                        : 'N/A'}
                    </div>
                    <div className="text-sm text-muted-foreground">Avg Behind Pace</div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Key Insights */}
            {clubInsights && clubInsights.keyInsights && (
              <Card>
                <CardHeader>
                  <CardTitle>Key Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <div className={`text-3xl font-bold ${clubInsights.keyInsights.weekendVsWeekday > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {clubInsights.keyInsights.weekendVsWeekday > 0 ? '+' : ''}{clubInsights.keyInsights.weekendVsWeekday} min
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {clubInsights.keyInsights.weekendVsWeekdayText || `Weekend vs weekday difference`}
                      </div>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <div className="text-3xl font-bold text-orange-600">
                        +{clubInsights.keyInsights.avgGroupGap} min
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {clubInsights.keyInsights.avgGroupGapText || `Average gap between consecutive groups`}
                      </div>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <div className="text-3xl font-bold text-blue-600">
                        +{clubInsights.keyInsights.firstLastDiff} min
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {clubInsights.keyInsights.firstLastDiffText || `First group vs last group difference`}
                      </div>
                    </div>
                  </div>
                  {/* Additional context */}
                  <div className="mt-4 pt-4 border-t text-center text-sm text-muted-foreground">
                    Based on {clubInsights.keyInsights.totalRounds || 0} total rounds
                    {clubInsights.keyInsights.weekdayAvg && clubInsights.keyInsights.weekendAvg && (
                      <span className="ml-2">
                        • Weekday avg: {clubInsights.keyInsights.weekdayAvg} min
                        • Weekend avg: {clubInsights.keyInsights.weekendAvg} min
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tee Time Performance */}
            {clubInsights && clubInsights.teeTimePerformance && clubInsights.teeTimePerformance.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Tee Time Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left py-3 px-4 font-medium">Tee Time</th>
                          <th className="text-center py-3 px-4 font-medium">Rounds</th>
                          <th className="text-center py-3 px-4 font-medium">Pace of Play</th>
                          <th className="text-center py-3 px-4 font-medium">Avg Finish</th>
                          <th className="text-center py-3 px-4 font-medium">Pace +/-</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clubInsights.teeTimePerformance.map((slot, index) => {
                          // Parse behind minutes from pacePlus string (e.g., "+5.3 min" -> 5.3)
                          const behindMin = parseFloat(slot.pacePlus?.replace(/[^\d.-]/g, '')) || 0;
                          const ratingClass = behindMin <= 5 ? 'success' : behindMin <= 10 ? 'warning' : 'danger';
                          return (
                            <tr key={index} className={`border-b ${getRowClass(ratingClass)}`}>
                              <td className="py-3 px-4 font-medium">{slot.teeTime}</td>
                              <td className="py-3 px-4 text-center">{slot.count}</td>
                              <td className="py-3 px-4 text-center">{slot.paceOfPlay}</td>
                              <td className="py-3 px-4 text-center">{slot.finishTime}</td>
                              <td className={`py-3 px-4 text-center font-semibold ${
                                ratingClass === 'success' ? 'text-green-600' :
                                ratingClass === 'warning' ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {slot.pacePlus}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* How Pace Ratings Work */}
            <Card>
              <CardHeader>
                <CardTitle>How Pace Ratings Work</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <p>
                  The Pace of Play (POP) rating system measures how well players maintain pace relative to expected times.
                  Lower ratings indicate faster play. The rating is calculated using three primary factors:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg border bg-card">
                    <h4 className="font-semibold mb-2">1. Gap Behind Group Ahead</h4>
                    <p className="text-muted-foreground text-xs">
                      Measures the time gap between your group and the group ahead at the finish.
                      Ideally, groups should finish close together without large gaps.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg border bg-card">
                    <h4 className="font-semibold mb-2">2. Total Round Time</h4>
                    <p className="text-muted-foreground text-xs">
                      Your actual playing time from first tee to last hole.
                      This is compared to the expected pace for the course and conditions.
                    </p>
                  </div>
                  <div className="p-4 rounded-lg border bg-card">
                    <h4 className="font-semibold mb-2">3. Expected Pace</h4>
                    <p className="text-muted-foreground text-xs">
                      Based on course layout, event type, and tee time.
                      Early groups typically have faster expected times than later groups.
                    </p>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-semibold mb-2">Rating Scale</h4>
                  <div className="flex flex-wrap gap-4 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded bg-green-500"></span>
                      <span><strong>0.00 or less</strong> - On pace or faster</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded bg-yellow-500"></span>
                      <span><strong>0.01 to 1.00</strong> - Slightly behind pace</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded bg-red-500"></span>
                      <span><strong>1.01+</strong> - Significantly behind pace</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Top-20 Leaderboards */}
            {clubInsights && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top-20 POP Index */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top-20: Pace of Play Index</CardTitle>
                    <p className="text-xs text-muted-foreground">Min. 10 rounds played</p>
                  </CardHeader>
                  <CardContent>
                    {clubInsights.topPOPIndex && clubInsights.topPOPIndex.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-center py-2 px-2 font-medium w-10">#</th>
                              <th className="text-left py-2 px-2 font-medium">Player</th>
                              <th className="text-center py-2 px-2 font-medium">Rounds</th>
                              <th className="text-center py-2 px-2 font-medium">POP Index</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clubInsights.topPOPIndex.map((player, index) => {
                              // popIndex is already a formatted string like "+0.14" or "-0.64"
                              const numericValue = parseFloat(player.popIndex);
                              return (
                                <tr key={player.ghin || index} className="border-b hover:bg-muted/50">
                                  <td className="py-2 px-2 text-center font-semibold text-muted-foreground">{index + 1}</td>
                                  <td className="py-2 px-2 font-medium">{player.name}</td>
                                  <td className="py-2 px-2 text-center">{player.rounds}</td>
                                  <td className={`py-2 px-2 text-center font-semibold ${
                                    numericValue <= 0 ? 'text-green-600' :
                                    numericValue <= 1 ? 'text-yellow-600' : 'text-red-600'
                                  }`}>
                                    {player.popIndex}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <p>No players with 10+ rounds found.</p>
                      </div>
                    )}
                    <div className="mt-3 text-center">
                      <button
                        onClick={() => setSelectedView('members-list')}
                        className="text-sm text-primary hover:underline"
                      >
                        View Complete List
                      </button>
                    </div>
                  </CardContent>
                </Card>

                {/* Top-20 Fastest Avg Round Times */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top-20: Fastest Avg Round Times</CardTitle>
                    <p className="text-xs text-muted-foreground">Min. 10 rounds played</p>
                  </CardHeader>
                  <CardContent>
                    {clubInsights.topFastestTimes && clubInsights.topFastestTimes.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-center py-2 px-2 font-medium w-10">#</th>
                              <th className="text-left py-2 px-2 font-medium">Player</th>
                              <th className="text-center py-2 px-2 font-medium">Rounds</th>
                              <th className="text-center py-2 px-2 font-medium">Avg Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clubInsights.topFastestTimes.map((player, index) => (
                              <tr key={player.ghin || index} className="border-b hover:bg-muted/50">
                                <td className="py-2 px-2 text-center font-semibold text-muted-foreground">{index + 1}</td>
                                <td className="py-2 px-2 font-medium">{player.name}</td>
                                <td className="py-2 px-2 text-center">{player.rounds}</td>
                                <td className="py-2 px-2 text-center font-semibold text-green-600">
                                  {player.avgTime}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <p>No players with 10+ rounds found.</p>
                      </div>
                    )}
                    <div className="mt-3 text-center">
                      <button
                        onClick={() => setSelectedView('members-list')}
                        className="text-sm text-primary hover:underline"
                      >
                        View Complete List
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Recent Events Tab */}
          <TabsContent value="recent-events" className="space-y-6">
            {/* How to Read These Ratings - Info Panel */}
            <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                  <Info className="h-5 w-5" />
                  How to Read These Ratings
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-3 text-blue-900 dark:text-blue-200">
                <blockquote className="border-l-4 border-blue-400 pl-4 italic">
                  "Pace of play is one of the most important factors in making golf enjoyable for everyone on the course."
                </blockquote>
                <div className="space-y-2">
                  <p className="font-semibold">Event "Min Behind" = Raw minutes behind schedule:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>+5.00</strong> = Event finished 5 minutes behind expected pace</li>
                    <li><strong className="text-green-600">0-10 min</strong> = On pace (green)</li>
                    <li><strong className="text-yellow-600">10-15 min</strong> = Slightly behind (yellow)</li>
                    <li><strong className="text-red-600">15+ min</strong> = Behind pace (red)</li>
                  </ul>
                </div>
                <div className="space-y-2 pt-2 border-t border-blue-200">
                  <p className="font-semibold">Player "POP Index" = Normalized comparison score:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>0.00</strong> = Average pace compared to other players</li>
                    <li><strong className="text-green-600">Negative (-)</strong> = Faster than average players</li>
                    <li><strong className="text-red-600">Positive (+)</strong> = Slower than average players</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Recent Events Table */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Events</CardTitle>
              </CardHeader>
              <CardContent>
                {recentEvents.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left py-3 px-4 font-medium">Event</th>
                          <th className="text-center py-3 px-4 font-medium">First</th>
                          <th className="text-center py-3 px-4 font-medium">Avg</th>
                          <th className="text-center py-3 px-4 font-medium">Last</th>
                          <th className="text-center py-3 px-4 font-medium">POP Rating</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentEvents.map((event, index) => (
                          <tr key={index} className={`border-b ${getRowClass(event.ratingClass)}`}>
                            <td className="py-3 px-4">
                              <button
                                onClick={() => handleEventClick(event)}
                                className="text-left hover:text-blue-600"
                              >
                                <div className="font-medium hover:underline">{event.eventName}</div>
                                <div className="text-xs text-muted-foreground">
                                  {new Date(event.eventDate).toLocaleDateString()} • {event.eventType}
                                </div>
                              </button>
                            </td>
                            <td className="py-3 px-4 text-center">{event.firstTime}</td>
                            <td className="py-3 px-4 text-center">{event.avgTime}</td>
                            <td className="py-3 px-4 text-center">{event.lastTime}</td>
                            <td className={`py-3 px-4 text-center font-semibold ${
                              event.ratingClass === 'success' ? 'text-green-600' :
                              event.ratingClass === 'warning' ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {event.popRating}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No recent events found.</p>
                    <p className="text-sm">Sync pace data to see event history.</p>
                  </div>
                )}

                {/* Club-wide Average Footer */}
                {analytics && (
                  <div className="mt-4 pt-4 border-t text-center">
                    <span className="text-muted-foreground">Club-wide Average: </span>
                    <span className={`font-semibold ${
                      (analytics.courseAnalytics.averageBehind || 0) <= 5 ? 'text-green-600' :
                      (analytics.courseAnalytics.averageBehind || 0) <= 10 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {analytics.courseAnalytics.averageBehind !== undefined
                        ? (analytics.courseAnalytics.averageBehind > 0 ? '+' : '') +
                          (analytics.courseAnalytics.averageBehind / 60).toFixed(2)
                        : '+0.76'}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="space-y-6">
            {/* Info Panel */}
            <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                  <Info className="h-5 w-5" />
                  Member Pace Rankings
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-blue-900 dark:text-blue-200">
                <p>
                  Click on any column header to sort the table. <strong>POP Rank</strong> is based on average time behind pace -
                  lower ranks indicate faster players. Players with fewer than 3 recorded rounds may have less reliable rankings.
                </p>
              </CardContent>
            </Card>

            {/* Members Table */}
            <Card>
              <CardHeader>
                <CardTitle>All Members ({membersList.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {sortedMembers.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th
                            className="text-left py-3 px-3 font-medium cursor-pointer hover:bg-muted"
                            onClick={() => handleSort('ghin')}
                          >
                            GHIN <SortIcon column="ghin" />
                          </th>
                          <th
                            className="text-left py-3 px-3 font-medium cursor-pointer hover:bg-muted"
                            onClick={() => handleSort('name')}
                          >
                            Player <SortIcon column="name" />
                          </th>
                          <th
                            className="text-center py-3 px-3 font-medium cursor-pointer hover:bg-muted"
                            onClick={() => handleSort('paceRating')}
                          >
                            Pace Rating <SortIcon column="paceRating" />
                          </th>
                          <th
                            className="text-center py-3 px-3 font-medium cursor-pointer hover:bg-muted"
                            onClick={() => handleSort('roundsPlayed')}
                          >
                            Rounds <SortIcon column="roundsPlayed" />
                          </th>
                          <th
                            className="text-center py-3 px-3 font-medium cursor-pointer hover:bg-muted"
                            onClick={() => handleSort('startDate')}
                          >
                            Start <SortIcon column="startDate" />
                          </th>
                          <th
                            className="text-center py-3 px-3 font-medium cursor-pointer hover:bg-muted"
                            onClick={() => handleSort('endDate')}
                          >
                            End <SortIcon column="endDate" />
                          </th>
                          <th
                            className="text-center py-3 px-3 font-medium cursor-pointer hover:bg-muted"
                            onClick={() => handleSort('avgPace')}
                          >
                            Avg Pace <SortIcon column="avgPace" />
                          </th>
                          <th
                            className="text-center py-3 px-3 font-medium cursor-pointer hover:bg-muted"
                            onClick={() => handleSort('popIndexRaw')}
                          >
                            POP Index <SortIcon column="popIndexRaw" />
                          </th>
                          <th
                            className="text-center py-3 px-3 font-medium cursor-pointer hover:bg-muted"
                            onClick={() => handleSort('behindRank')}
                          >
                            POP Rank <SortIcon column="behindRank" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedMembers.map((member, index) => (
                          <tr key={member.ghin || index} className={`border-b ${getRowClass(member.ratingClass)}`}>
                            <td className="py-3 px-3 font-mono text-xs">{member.ghin}</td>
                            <td className="py-3 px-3 font-medium">
                              <button
                                onClick={() => handleMemberClick(member)}
                                className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                              >
                                {member.name}
                              </button>
                            </td>
                            <td className={`py-3 px-3 text-center font-medium ${
                              member.ratingClass === 'success' ? 'text-green-600' :
                              member.ratingClass === 'warning' ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {member.paceRating}
                            </td>
                            <td className="py-3 px-3 text-center">{member.roundsPlayed}</td>
                            <td className="py-3 px-3 text-center text-xs">
                              {member.startDate ? new Date(member.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                            </td>
                            <td className="py-3 px-3 text-center text-xs">
                              {member.endDate ? new Date(member.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                            </td>
                            <td className="py-3 px-3 text-center">{member.avgPace}</td>
                            <td className={`py-3 px-3 text-center font-semibold ${
                              member.popIndexRaw <= 0 ? 'text-green-600' :
                              member.popIndexRaw <= 1 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {member.popIndex}
                            </td>
                            <td className="py-3 px-3 text-center font-semibold">
                              {member.behindRank || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No members found.</p>
                    <p className="text-sm">Sync pace data to see member rankings.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Member Detail Panel */}
        {selectedMember && (
          <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
            <div className="bg-background w-full max-w-2xl h-full overflow-y-auto shadow-xl">
              {/* Panel Header */}
              <div className="sticky top-0 bg-background border-b p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <User className="h-6 w-6 text-muted-foreground" />
                  <div>
                    <h2 className="text-xl font-bold">{selectedMember.name}</h2>
                    <p className="text-sm text-muted-foreground">GHIN: {selectedMember.ghin}</p>
                  </div>
                </div>
                <button
                  onClick={closeMemberDetail}
                  className="p-2 hover:bg-muted rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Panel Content */}
              <div className="p-4 space-y-6">
                {loadingDetail ? (
                  <div className="space-y-4">
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-40 w-full" />
                  </div>
                ) : memberDetail ? (
                  <>
                    {/* POP Index Summary */}
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">POP Index</p>
                            <p className={`text-4xl font-bold ${
                              memberDetail.popIndexRaw <= 0 ? 'text-green-600' :
                              memberDetail.popIndexRaw <= 1 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {memberDetail.popIndex}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Rank</p>
                            <p className="text-2xl font-semibold">#{memberDetail.popIndexRank || '-'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Rounds</p>
                            <p className="text-2xl font-semibold">{memberDetail.totalRounds}</p>
                            <p className="text-xs text-muted-foreground">
                              ({memberDetail.roundsUsedForPOP} used for POP)
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Rounds Table */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Round History</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {memberDetail.rounds && memberDetail.rounds.length > 0 ? (
                          <>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b bg-muted/50">
                                    <th className="text-left py-2 px-2 font-medium">Event</th>
                                    <th className="text-center py-2 px-2 font-medium">Date</th>
                                    <th className="text-center py-2 px-2 font-medium">Start</th>
                                    <th className="text-center py-2 px-2 font-medium">Finish</th>
                                    <th className="text-center py-2 px-2 font-medium">Pace</th>
                                    <th className="text-center py-2 px-2 font-medium">Gap</th>
                                    <th className="text-center py-2 px-2 font-medium">Rating</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {memberDetail.rounds.map((round, index) => (
                                    <tr
                                      key={index}
                                      className={`border-b ${
                                        round.popIndexRaw === null || round.popIndexRaw === undefined ? '' :
                                        round.popIndexRaw <= 0 ? 'bg-green-50 dark:bg-green-950/30' :
                                        round.popIndexRaw <= 1 ? 'bg-yellow-50 dark:bg-yellow-950/30' :
                                        'bg-red-50 dark:bg-red-950/30'
                                      }`}
                                    >
                                      <td className="py-2 px-2">
                                        {round.usedInPOP && <span className="text-blue-600 font-bold">★ </span>}
                                        {round.eventName}
                                      </td>
                                      <td className="text-center py-2 px-2 text-xs">
                                        {round.date ? new Date(round.date).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: '2-digit'
                                        }) : '-'}
                                      </td>
                                      <td className="text-center py-2 px-2">{round.start || '-'}</td>
                                      <td className="text-center py-2 px-2">{round.finish || '-'}</td>
                                      <td className="text-center py-2 px-2">{round.pace || '-'}</td>
                                      <td className="text-center py-2 px-2">{round.gap || '-'}</td>
                                      <td className={`text-center py-2 px-2 font-semibold ${
                                        round.popIndexRaw === null || round.popIndexRaw === undefined ? '' :
                                        round.popIndexRaw <= 0 ? 'text-green-600' :
                                        round.popIndexRaw <= 1 ? 'text-yellow-600' : 'text-red-600'
                                      }`}>
                                        {round.popIndex || '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <p className="text-xs text-muted-foreground mt-4">
                              <span className="text-blue-600 font-bold">★</span> Rounds marked with a star are used to determine your POP Index
                            </p>
                          </>
                        ) : (
                          <p className="text-center text-muted-foreground py-4">
                            No round data available
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Last Synced */}
                    {memberDetail.lastSynced && (
                      <p className="text-xs text-muted-foreground text-center">
                        Last synced: {new Date(memberDetail.lastSynced).toLocaleString()}
                      </p>
                    )}
                  </>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Failed to load member details. Please try again.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Event Detail Panel */}
        {selectedEvent && (
          <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
            <div className="bg-background w-full max-w-3xl h-full overflow-y-auto shadow-xl">
              {/* Panel Header */}
              <div className="sticky top-0 bg-background border-b p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className="h-6 w-6 text-muted-foreground" />
                  <div>
                    <h2 className="text-xl font-bold">{selectedEvent.eventName}</h2>
                    <p className="text-sm text-muted-foreground">
                      {new Date(selectedEvent.eventDate).toLocaleDateString()} • {selectedEvent.eventType}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeEventDetail}
                  className="p-2 hover:bg-muted rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Panel Content */}
              <div className="p-4 space-y-6">
                {loadingEventDetail ? (
                  <div className="space-y-4">
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-40 w-full" />
                    <Skeleton className="h-40 w-full" />
                  </div>
                ) : eventDetail ? (
                  <>
                    {/* Event Summary */}
                    <Card>
                      <CardContent className="pt-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground">Players</p>
                            <p className="text-2xl font-bold">{eventDetail.stats?.totalPlayers || 0}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground">Groups</p>
                            <p className="text-2xl font-bold">{eventDetail.eventInfo?.groupCount || 0}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground">First Finish</p>
                            <p className="text-xl font-semibold">{eventDetail.stats?.firstFinish || '--'}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground">Last Finish</p>
                            <p className="text-xl font-semibold">{eventDetail.stats?.lastFinish || '--'}</p>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t text-center">
                          <p className="text-sm text-muted-foreground">Event POP Rating</p>
                          <p className={`text-3xl font-bold ${
                            eventDetail.stats?.avgPopIndexRaw <= 0 ? 'text-green-600' :
                            eventDetail.stats?.avgPopIndexRaw <= 1 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {eventDetail.stats?.avgPopIndex || '+0.00'}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Group-by-Group Breakdown */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Group-by-Group Pace Data</CardTitle>
                        <p className="text-xs text-muted-foreground">
                          Similar to Balboa's POP Events view - showing each tee time group's performance
                        </p>
                      </CardHeader>
                      <CardContent>
                        {eventDetail.groups && eventDetail.groups.length > 0 ? (
                          <div className="space-y-2">
                            {eventDetail.groups.map((group, groupIndex) => (
                              <div key={groupIndex} className={`border rounded-lg px-4 py-3 ${
                                group.groupRatingClass === 'success' ? 'bg-green-50 dark:bg-green-950/30 border-green-200' :
                                group.groupRatingClass === 'warning' ? 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200' :
                                group.groupRatingClass === 'danger' ? 'bg-red-50 dark:bg-red-950/30 border-red-200' : 'bg-muted/30'
                              }`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <span className="font-bold">Group {group.groupNumber}</span>
                                    <span className="text-sm text-muted-foreground">{group.teeTime}</span>
                                    <span className="text-sm">
                                      {group.players.map((player, idx) => (
                                        <span key={player.ghin}>
                                          <button
                                            onClick={() => {
                                              closeEventDetail();
                                              handleMemberClick({ ghin: player.ghin, name: player.name });
                                            }}
                                            className="text-blue-600 hover:text-blue-800 hover:underline"
                                          >
                                            {player.name}
                                          </button>
                                          {idx < group.players.length - 1 && ', '}
                                        </span>
                                      ))}
                                    </span>
                                  </div>
                                  {/* Group Rating from Balboa */}
                                  <div className={`text-lg font-bold ${
                                    group.totalRating === null || group.totalRating === undefined ? '' :
                                    group.totalRating <= 0 ? 'text-green-600' :
                                    group.totalRating <= 1 ? 'text-yellow-600' : 'text-red-600'
                                  }`}>
                                    {group.totalRating !== null && group.totalRating !== undefined
                                      ? (group.totalRating >= 0 ? `+${group.totalRating.toFixed(2)}` : group.totalRating.toFixed(2))
                                      : '--'}
                                  </div>
                                </div>
                                {/* Simple round time info */}
                                {group.roundTime && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    Round: {group.roundTime} • Finished: {group.players[0]?.finish || '--'}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-muted-foreground py-4">
                            No group data available for this event
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    {/* All Players Summary Table */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">All Players Summary</CardTitle>
                        <p className="text-xs text-muted-foreground">
                          Sorted by tee time
                        </p>
                      </CardHeader>
                      <CardContent>
                        {eventDetail.players && eventDetail.players.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b bg-muted/50">
                                  <th className="text-left py-2 px-2 font-medium">Player</th>
                                  <th className="text-center py-2 px-2 font-medium">Start</th>
                                  <th className="text-center py-2 px-2 font-medium">Finish</th>
                                  <th className="text-center py-2 px-2 font-medium">Pace</th>
                                  <th className="text-center py-2 px-2 font-medium">Gap</th>
                                  <th className="text-center py-2 px-2 font-medium">POP Index</th>
                                </tr>
                              </thead>
                              <tbody>
                                {eventDetail.players.map((player, index) => (
                                  <tr
                                    key={index}
                                    className={`border-b ${
                                      player.ratingClass === 'success' ? 'bg-green-50 dark:bg-green-950/30' :
                                      player.ratingClass === 'warning' ? 'bg-yellow-50 dark:bg-yellow-950/30' :
                                      player.ratingClass === 'danger' ? 'bg-red-50 dark:bg-red-950/30' : ''
                                    }`}
                                  >
                                    <td className="py-2 px-2">
                                      <button
                                        onClick={() => {
                                          closeEventDetail();
                                          handleMemberClick({ ghin: player.ghin, name: player.name });
                                        }}
                                        className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                                      >
                                        {player.name}
                                      </button>
                                    </td>
                                    <td className="py-2 px-2 text-center">{player.start}</td>
                                    <td className="py-2 px-2 text-center">{player.finish}</td>
                                    <td className="py-2 px-2 text-center">{player.pace}</td>
                                    <td className="py-2 px-2 text-center">{player.gap}</td>
                                    <td className={`py-2 px-2 text-center font-semibold ${
                                      player.popIndexRaw === null || player.popIndexRaw === undefined ? '' :
                                      player.popIndexRaw <= 0 ? 'text-green-600' :
                                      player.popIndexRaw <= 1 ? 'text-yellow-600' : 'text-red-600'
                                    }`}>
                                      {player.popIndex}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-center text-muted-foreground py-4">
                            No player data available
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Legend */}
                    <div className="p-4 bg-muted/50 rounded-lg text-xs">
                      <p className="font-semibold mb-2">Legend:</p>
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-green-500"></span>
                          <span>On pace or faster (POP Index ≤ 0.5)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-yellow-500"></span>
                          <span>Slightly behind (0.5 &lt; POP Index ≤ 1.0)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded bg-red-500"></span>
                          <span>Behind pace (POP Index &gt; 1.0)</span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Failed to load event details. Please try again.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        {analytics && (
          <div className="text-center text-sm text-muted-foreground pt-4 border-t">
            Data synced from Balboa Park Golf Club • Last updated: {new Date(analytics.lastUpdated || Date.now()).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}

export default PaceOfPlayManagement;
